import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { setup2fa } from "../api/auth";

type Props = {
  userId: number;
  provisioningUri: string | null;
  secret: string | null;
  onDone?: () => void;
};

export default function Setup2FAPage({ userId, provisioningUri, secret, onDone }: Props) {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!provisioningUri) {
      setQrDataUrl(null);
      return;
    }
    let cancelled = false;
    QRCode.toDataURL(provisioningUri, { margin: 1, width: 220 })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [provisioningUri]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await setup2fa(userId, code.trim());
      if (onDone) onDone();
      else window.location.reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-screen">
      <div className="auth-card auth-card-wide">
        <h1>Set up two-factor auth</h1>
        {provisioningUri && secret ? (
          <>
            <p>Scan this QR code with your authenticator app (1Password, Authy, Google Authenticator).</p>
            {qrDataUrl ? (
              <img className="auth-qr" src={qrDataUrl} alt="TOTP QR code" width={220} height={220} />
            ) : (
              <p className="auth-qr-pending">Generating QR…</p>
            )}
            <p className="auth-secret-label">Or enter this secret manually:</p>
            <code className="auth-secret">{secret}</code>
          </>
        ) : (
          <p>
            Your TOTP secret was provisioned when you registered. If you no
            longer have the QR code, ask an admin to reset your account.
          </p>
        )}
        <form onSubmit={submit}>
          <label>
            Enter the 6-digit code to confirm
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              required
              autoFocus
              autoComplete="one-time-code"
            />
          </label>
          {error && <p className="auth-error">{error}</p>}
          <button type="submit" disabled={busy || code.length !== 6}>
            {busy ? "Verifying…" : "Verify & continue"}
          </button>
        </form>
      </div>
    </div>
  );
}
